/**
 * preview-toggle.js — the preview switch, in row 2's right anchor.
 *
 * The last control the census gave a `row2` destination and nobody had moved:
 * CMD-3 and CMD-4 both named CMD-5 as its owner, CMD-5 cut it to keep its two
 * halves reviewable, and it sat in `row1.legacy` until now. It belongs beside
 * the viewport switcher because "which screen am I looking at" and "am I
 * looking at editing chrome" are the same question asked twice.
 *
 * ── Two glyphs, one button ────────────────────────────────────────────────
 * Ported from `ui/toolbar.js`, deliberately: the pressed state swaps play for
 * stop through CSS keyed on `aria-pressed`, so the accessible state and the
 * visible state cannot drift — there is no second assignment to forget. What
 * changes is construction: `createButton` + `icon()`, because the right anchor
 * has no sweep allowlist to hide in any more (CMD-5 shrank all four entries).
 */

import { eventBus } from '../../core/events.js';
import { createButton } from '../components.js';
import { icon } from '../icons.js';

export class PreviewToggle {
  /** @param {HTMLElement} container — the `row2.view` slot */
  constructor(container) {
    this._container = container;
    this._offs = [];
    this._render();
    this._listen();
  }

  get button() {
    return this._btn;
  }

  destroy() {
    for (const off of this._offs) if (typeof off === 'function') off();
    this._offs = [];
    this._btn = null;
  }

  _render() {
    // The id moves WITH the control (`tour-registry.js` walks the user to it,
    // and the census tracks it); re-homing a button is not deleting it.
    this._btn = createButton({
      variant: 'bare',
      className: 'pb-cb-icon pb-preview-toggle',
      id: 'pb-preview-btn',
      icon:
        icon('play', { className: 'pb-preview-icon-play' }) +
        icon('stop', { className: 'pb-preview-icon-stop' }),
      ariaLabel: 'Preview',
      tooltip: 'Preview',
      onClick: () => eventBus.emit('toolbar:toggle-preview')
    });
    this._btn.setAttribute('aria-pressed', 'false');
    this._container.appendChild(this._btn);
  }

  _listen() {
    this._offs.push(
      eventBus.on('preview:state-changed', ({ enabled }) => {
        if (!this._btn) return;
        const on = Boolean(enabled);
        this._btn.setAttribute('aria-pressed', String(on));
        this._btn.dataset.tooltip = on ? 'Exit Preview' : 'Preview';
        this._btn.setAttribute('aria-label', on ? 'Exit Preview' : 'Preview');
        // The canvas class stays this control's business, exactly as it was in
        // the legacy bar — preview mode is a canvas state, and moving the
        // toggle without moving the class would have left the chrome on.
        document.querySelector('.pb-iframe-wrap')?.classList.toggle('pb-preview-active', on);
      })
    );
  }
}
